// src/pages/ProjectEditPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, customersAPI } from '../services/api';

const ProjectEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    customer_id: '',
    title: '',
    description: '',
    status: 'planned'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Lade Projektdaten und Kunden für Bearbeitung');
        const [projectResponse, customersResponse] = await Promise.all([
          projectsAPI.getById(id),
          customersAPI.getAll()
        ]);
        
        setFormData({
          customer_id: projectResponse.data.project.customer_id,
          title: projectResponse.data.project.title,
          description: projectResponse.data.project.description || '',
          status: projectResponse.data.project.status
        });
        
        setCustomers(customersResponse.data.customers);
        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
        
        // Prüfen, ob es ein Authentifizierungsfehler ist
        if (err.response && err.response.status === 401) {
          // Manuell zur Login-Seite umleiten, wenn nicht authentifiziert
          console.log('Nicht authentifiziert, leite zur Login-Seite um');
          navigate('/login');
          return;
        }
        
        setError('Fehler beim Laden der Daten');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Aktualisiere Projekt:', formData);
      await projectsAPI.update(id, formData);
      console.log('Projekt erfolgreich aktualisiert');
      navigate(`/projekte/${id}`);
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Projekts:', err);
      
      // Prüfen, ob es ein Authentifizierungsfehler ist
      if (err.response && err.response.status === 401) {
        console.log('Nicht authentifiziert, leite zur Login-Seite um');
        navigate('/login');
        return;
      }
      
      setError('Fehler beim Aktualisieren des Projekts');
    }
  };

  if (loading) {
    return <div className="text-center py-6">Lade Projektdaten...</div>;
  }

  return (
    <div className="p-4 sm:p-0">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Projekt bearbeiten</h1>
        <button
          onClick={() => navigate(`/projekte/${id}`)}
          className="border border-gray-300 px-4 py-2 rounded text-gray-700 hover:bg-gray-100"
        >
          Abbrechen
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-md">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 mb-2">Kunde *</label>
              <select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">-- Kunden auswählen --</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.company_name ? `${customer.company_name} (${customer.first_name} ${customer.last_name})` : `${customer.first_name} ${customer.last_name}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Projekttitel *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="planned">Geplant</option>
                <option value="in_progress">In Bearbeitung</option>
                <option value="completed">Abgeschlossen</option>
                <option value="cancelled">Storniert</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Beschreibung</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded"
            >
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectEditPage;
